import { useState } from "react";
import VisibilityButton from "./visibility-button";
import { Table } from "@tanstack/react-table";
import { Box } from "@mui/material";

export interface DataTableTopBarProps<TData> {
  name: string;
  type: "server" | "client" | "none";
  rowCount?: number;
  showRowCount?: boolean;
  showColumnVisibility?: boolean;
  table: Table<TData>;
  dividerColor?: string;
  paperBackground?: string;
  actions: React.ReactNode[];
  defaultColumns?: string[];
  perPage?: number;
  selectedRowsCount?: number;
  pageIndex?: number;
  extraActions?: React.ReactNode;
  extraRightActions?: React.ReactNode;
}

export default function DataTableTopBar<TData>({
  name,
  type,
  rowCount,
  showRowCount,
  showColumnVisibility,
  table,
  dividerColor = "#E0E0E0",
  paperBackground = "#FFFFFF",
  actions,
  defaultColumns = [],
  perPage,
  selectedRowsCount = 0,
  pageIndex = 0,
  extraActions,
  extraRightActions,
}: DataTableTopBarProps<TData>) {
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);

  const getCountDisplay = () => {
    if (!showRowCount) return null;
    if (selectedRowsCount > 0) {
      return (
        <span style={{ fontSize: "14px", fontWeight: "500" }}>
          <strong>{selectedRowsCount}</strong> Selected Out of {rowCount || 0}{" "}
          {name}
        </span>
      );
    }

    if (type === "server" && perPage && rowCount) {
      const start = pageIndex * perPage + 1;
      const end = Math.min((pageIndex + 1) * perPage, rowCount);
      return (
        <span style={{ fontSize: "14px", fontWeight: "500" }}>
          {start} - {end} of {rowCount} {name}
        </span>
      );
    }

    return (
      <span style={{ fontSize: "14px", fontWeight: "600" }}>
        {type === "server"
          ? rowCount ?? 0
          : table.getFilteredRowModel().rows.length}{" "}
        {name}
      </span>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {extraActions}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: 4,
          padding: "16px",
          height: "56px",
          flexShrink: 0,
          borderBottom: `1px solid ${dividerColor}`,
          backgroundColor: paperBackground,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexDirection: "column",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {getCountDisplay()}
            {actions.map((action) => action)}
          </Box>
        </Box>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {extraRightActions}
          {showColumnVisibility && (
            <VisibilityButton<TData>
              key="visibility-button"
              visibility={visibilityDialogOpen}
              setVisibility={setVisibilityDialogOpen}
              table={table}
              defaultColumns={defaultColumns}
            />
          )}
        </div>
      </div>
    </Box>
  );
}
